// Real customer contacts never have a real profile picture on file (WhatsApp doesn't
// expose one at all; Instagram's is only ever fetched ephemerally for a story-reply
// card, never persisted — see the contact-pfp-audit-2026-08-18 memory) — these are the
// deliberate mock-avatar images for CONTACTS specifically, not team members (who keep
// nameToEmoji/stringAvatar as before).
import contactAvatar1 from "../assets/avatars/avatar-1.png";
import contactAvatar2 from "../assets/avatars/avatar-2.png";
import contactAvatar3 from "../assets/avatars/avatar-3.png";
import contactAvatar4 from "../assets/avatars/avatar-4.png";
import contactAvatar5 from "../assets/avatars/avatar-5.png";
import contactAvatar6 from "../assets/avatars/avatar-6.png";
import contactAvatar7 from "../assets/avatars/avatar-7.png";

const CONTACT_EMOJIS = ["😀","🙂","☺️","😚","😊","😴","😦","🤗","😎","🤠",];

export const nameToEmoji = (name) => {
  if (!name) return "✨";
  let hash = 0;
  const s = String(name).trim();
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CONTACT_EMOJIS[Math.abs(hash) % CONTACT_EMOJIS.length];
};

// Same deterministic name-hash approach as nameToEmoji, just picking from these 7
// mascot images instead of an emoji.
const CONTACT_AVATAR_IMAGES = [
  contactAvatar1,
  contactAvatar2,
  contactAvatar3,
  contactAvatar4,
  contactAvatar5,
  contactAvatar6,
  contactAvatar7,
];

export const nameToContactAvatarImage = (name) => {
  if (!name) return CONTACT_AVATAR_IMAGES[0];
  let hash = 0;
  const s = String(name).trim();
  for (let i = 0; i < s.length; i++) {
    hash = s.charCodeAt(i) + ((hash << 5) - hash);
  }
  return CONTACT_AVATAR_IMAGES[Math.abs(hash) % CONTACT_AVATAR_IMAGES.length];
};

// Spread onto MUI <Avatar imgProps={...}> (or a plain <img {...}>) wherever a
// mascot avatar image renders — without this, the image is a normal draggable/
// selectable browser element, so a user can drag it (Chrome turns an in-page
// <img> drag into a real synthetic File) straight into the message composer's
// drop zone, or right-click "Copy Image" + paste it, and it gets ingested and
// sent as a real attachment (Rahim, 2026-08-27: "a user can select the pfp
// image... and can literally send it as a message"). Single shared constant,
// not copy-pasted per call site, so every avatar spot stays consistent.
export const NON_DRAGGABLE_AVATAR_IMG_PROPS = {
  draggable: false,
  onDragStart: (e) => e.preventDefault(),
  style: { WebkitUserDrag: "none", userSelect: "none" },
};

export const stringToColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    // Blend towards white by increasing the value proportionally
    const lighterValue = Math.floor(value * 0.6 + 255 * 0.4); // Blend 60% original, 40% white
    color += `00${lighterValue.toString(16)}`.slice(-2);
  }
  return color;
};

// Generate vibrant color without lightening (for team members to match user conversations)
export const stringToVibrantColor = (string) => {
  let hash = 0;
  for (let i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }
  let color = "#";
  for (let i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    // Ensure minimum brightness for visibility but keep colors vibrant
    // Use 70% original, 30% white for better visibility while maintaining vibrancy
    const vibrantValue = Math.floor(value * 0.7 + 255 * 0.3);
    color += `00${vibrantValue.toString(16)}`.slice(-2);
  }
  return color;
};

// Avatar sizes that can be used throughout the app
export const AVATAR_SIZES = {
  SMALL: 32, // For smaller components like ChatboxNavbar
  MEDIUM: 40, // Default medium size
  LARGE: 48, // Larger size for profile views
  XLARGE: 64, // Extra large for profile pages
};

/**
 * Generate avatar props with responsive sizing
 * @param {string} name - The name to generate initials from
 * @param {string} size - Size key from AVATAR_SIZES or custom number
 * @returns {object} Avatar props including sx and children
 */
const fontSizeFromSizeValue = (sizeValue) => {
  if (typeof sizeValue === "object" && sizeValue !== null && !Array.isArray(sizeValue)) {
    const out = {};
    for (const [bp, val] of Object.entries(sizeValue)) {
      if (typeof val === "number") {
        out[bp] = `${Math.round(val * 0.4 * 10) / 10}px`;
      }
    }
    return out;
  }
  const n = Number(sizeValue);
  return `${(Number.isFinite(n) ? n : AVATAR_SIZES.MEDIUM) * 0.4}px`;
};

export const stringAvatar = (name, size = AVATAR_SIZES.MEDIUM) => {
  const sizeValue =
    typeof size === "string" ? AVATAR_SIZES[size] || AVATAR_SIZES.MEDIUM : size;

  const fontSize = fontSizeFromSizeValue(sizeValue);

  // Handle null or undefined name
  if (!name) {
    return {
      sx: {
        bgcolor: "#ccc", // Default gray color
        width: sizeValue,
        height: sizeValue,
        color: "Black",
        fontWeight: "500",
        fontSize,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      },
      children: "?",
    };
  }

  const trimmed = String(name).trim();
  const initials = trimmed.length
    ? trimmed[0].toLocaleUpperCase()
    : "?";

  return {
    sx: {
      bgcolor: stringToColor(name), // Lightened background color
      width: sizeValue, // Dynamic avatar size
      height: sizeValue,
      color: "Black", // Text color
      fontWeight: "500", // Increase font weight
      fontSize,
      display: "flex",
      justifyContent: "center", // Center initials horizontally
      alignItems: "center", // Center initials vertically
    },
    children: initials,
  };
};
